import { string, z } from "zod";
import { createApiResponseSchema } from "../common/pagination";

export const FormalityRequestSchema = z.object({
  id: z.string(),
  applicant_id: z.string(),
  code: z.string(),
  type: z.string(),
  name: z.string(),
  date: z.iso.datetime().nullable(),
  status: z.string(),
  status_label: z.string().optional(),
  description: z.string().nullable(),
  flow_execution_id: z.string().nullable(),
  flowExecution: z.object({
    id: z.string(),
    flow_id: z.string(),
  }),
  group_id: z.string().nullable(),
  groups: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string().nullable(),
      pivot: z.object({
        assigned_user: z
          .object({
            id: z.string(),
            name: z.string(),
            last_name: z.string(),
            position: z
              .object({
                id: z.string(),
                name: z.string(),
              })
              .optional(),
            email: z.email(),
          })
          .nullable(),
        assigned_at: z.iso.datetime().nullable(),
        created_at: z.iso.datetime().nullable(),
      }),
    }),
  ),
  applicant: z
    .object({
      id: z.string(),
      first_name: z.string(),
      last_name: z.string(),
      email: z.email(),
    })
    .nullable()
    .optional(),
  assignedTo: z
    .object({
      id: z.string(),
      name: z.string(),
      last_name: z.string(),
      position: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .optional(),
      email: z.email(),
    })
    .nullable(),
  process: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    status: z.string().optional(),
    three_code_identifier: z.string().nullable(),
    estimated_cost_min: z.number().nullable(),
    estimated_cost_max: z.number().nullable(),
    external_code: z.string().nullable(),
    estimated_time_min: z.number().nullable(),
    estimated_time_max: z.number().nullable(),
    estimated_time_min_unit: z.string().nullable(),
    estimated_time_max_unit: z.string().nullable(),
    estimated_steps_min: z.number().nullable(),
    estimated_steps_max: z.number().nullable(),
    created_at: z.iso.datetime().nullable(),
    required_documents: z.array(z.string()).nullable(),
    max_flows: z.number().nullable(),
    reason: z.string().nullable().optional(),
    created_by: z.string().nullable(),
    approved: z.boolean().nullable(),
    approved_at: z.iso.datetime().nullable(),
    updated_at: z.iso.datetime().nullable(),
    tags: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
        }),
      )
      .nullable(),
  }),
  created_at: z.iso.datetime().nullable(),
  updated_at: z.iso.datetime().nullable(),
  is_express: z.boolean().optional(),
  documents: z
    .array(
      z.looseObject({
        node_id: z.string(),
        field_id: z.string(),
        field_name: z.string().optional(),
        field_label: z.string().optional(),
      }), // Allow additional metadata properties
    )
    .optional(),
  first_assigned_at: z.string().datetime().nullable(),
  cancellation_requests: z
    .array(
      z.object({
        id: z.string(),
        formality_request_id: z.string(),
        reason: z.string().nullable(),
        requested_by_id: z.string(),
        requested_by_type: z.string(),
        requested_at: z.string().datetime(),
        status: z.string().nullable(),
        approved_at: z.string().datetime().nullable(),
        denied_at: z.string().datetime().nullable(),
        observation: z.string().nullable(),
        created_by: z.object({
          first_name: z.string().nullable().optional(),
          last_name: z.string().nullable().optional(),
        }),
      }),
    )
    .optional()
    .nullable(),
  current_node: z
    .object({
      display_name: z.string(),
      id: z.string(),
      ref_name: z.string(),
      type: z.string(),
    })
    .optional()
    .nullable(),
});

export type FormalityRequestType = z.infer<typeof FormalityRequestSchema>;

export const ApiFormalityRequestResponseSchema = createApiResponseSchema(
  FormalityRequestSchema,
);
export type ApiFormalityRequestResponseType = z.infer<
  typeof ApiFormalityRequestResponseSchema
>;

export const FormalityFormSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  status: z.enum(["published"]),
  created_at: z.iso.datetime().nullable(),
  updated_at: z.iso.datetime().nullable(),
  deleted_at: z.iso.datetime().nullable(),
  form_assignments: z.array(
    z.object({
      id: z.string(),
      status: z.enum([
        "ASSIGNED",
        "IN_PROGRESS",
        "COMPLETED",
        "SUBMITTED",
        "CORRECTION",
        "FILL_CORRECTION",
        "FAILED_CORRECTION",
      ]),
      filled_form_id: z.string().nullable(),
      assigned_at: z.iso.datetime().nullable(),
      due_date: z.iso.datetime().nullable(),
      revisions: z.array(
        z.object({
          internal_deadline: z.string().nullable(),
          internal_started_at: z.iso.datetime().nullable(),
        }),
      ),
    }),
  ),
});

export type FormalityFormType = z.infer<typeof FormalityFormSchema>;

export const ApiFormalityFormResponseSchema =
  createApiResponseSchema(FormalityFormSchema);
export type ApiFormalityFormResponseType = z.infer<
  typeof ApiFormalityFormResponseSchema
>;

export const FormSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  status: z.string(),
  steps: z.array(
    z.object({
      id: string(),
      name: string(),
      description: string().nullable(),
      order: z.number(),
      sections: z.array(
        z.object({
          id: string(),
          name: string(),
          title: string(),
          description: string().nullable(),
          fields: z.array(
            z.object({
              id: string(),
              name: string(),
              label: string(),
              type: z.enum([
                "text",
                "number",
                "file",
                "paragraph",
                "textarea",
                "select",
                "date",
                "dateRange",
                "dui_nit",
                "datetime_range",
                "date_range",
                "interoperability",
                "switch",
              ]),
              required: z.boolean(),
              is_preventable: z.boolean().optional(),
              config: z.any().nullable(),
              filled_fields: z
                .array(
                  z.object({
                    id: string(),
                    label: z.string().optional(),
                    created_at: z.iso.datetime(),
                    updated_at: z.iso.datetime(),
                    files: z.array(
                      z.object({
                        id: string(),
                        name: string(),
                        file_name: string(),
                        url: string(),
                        size_formatted: string(),
                      }),
                    ),
                    field_id: string(),
                    correction: z.object({
                      id: string(),
                      status: z.enum(["APPROVED", "DENIED", "IN_PROCESS"]),
                      revision: z.object({
                        id: string(),
                      }),
                      correction: z.object({
                        revision_id: string(),
                        group: z
                          .object({
                            id: string(),
                            name: string(),
                          })
                          .optional()
                          .nullable(),
                      }),
                      comments: z.array(
                        z.object({
                          text: z.string(),
                          created_by: z.object({
                            id: z.string(),
                          }),
                        }),
                      ),
                    }),
                    value: z
                      .string()
                      .or(z.number())
                      .or(z.array(z.string()))
                      .or(
                        z.object({
                          start: z.string(),
                          end: z.string(),
                        }),
                      ),
                  }),
                )
                .nullable(),
            }),
          ),
        }),
      ),
    }),
  ),
  created_at: z.iso.datetime().nullable(),
  updated_at: z.iso.datetime().nullable(),
  deleted_at: z.iso.datetime().nullable(),
  form_assignments: z.array(
    z.object({
      id: z.string(),
      status: z.enum([
        "ASSIGNED",
        "IN_PROGRESS",
        "COMPLETED",
        "SUBMITTED",
        "CORRECTION",
        "FILL_CORRECTION",
        "FAILED_CORRECTION",
      ]),
      assigned_at: z.iso.datetime().nullable(),
      due_date: z.iso.datetime().nullable(),
      revisions: z.array(
        z.object({
          id: z.string(),
          status: z.string(),
          applicant_form_assignment_id: z.string().nullable(),
          internal_deadline: z.iso.datetime().nullable(),
          applicant_form_assignment: z
            .object({
              id: z.string(),
              status: z.string(),
            })
            .nullable(),
          corrections: z.array(
            z.object({
              id: z.string(),
              revision_id: z.string(),
              status: z.enum(["APPROVED", "DENIED", "IN_PROCESS"]),
              field_corrections: z.array(
                z.object({
                  id: z.string(),
                  correction_id: z.string(),
                  status: z.enum(["APPROVED", "DENIED", "IN_PROCESS"]),
                  created_at: z.iso.datetime(),
                  comments: z.array(
                    z.object({
                      text: z.string(),
                      created_by: z.object({
                        id: z.string(),
                      }),
                      creator: z.object({
                        name: z.string(),
                        last_name: z.string(),
                      }),
                    }),
                  ),
                  filled_field: z
                    .object({
                      field_id: z.string(),
                      value: z.string().or(z.object()),
                    })
                    .optional(),
                  filled_field_id: z.string(),
                  filled_fields: z
                    .array(
                      z.object({
                        id: string(),
                        value: z
                          .string()
                          .or(z.number())
                          .or(z.array(z.string()))
                          .or(
                            z.object({
                              start: z.string(),
                              end: z.string(),
                            }),
                          ),
                      }),
                    )
                    .nullable(),
                }),
              ),
              group_id: z.string().nullable(),
              comments: z.array(
                z.object({
                  text: z.string(),
                }),
              ),
            }),
          ),
        }),
      ),
    }),
  ),
  user_form_assignments: z.array(
    z.object({
      id: z.string(),
      status: z.enum([
        "ASSIGNED",
        "IN_PROGRESS",
        "COMPLETED",
        "SUBMITTED",
        "CORRECTION",
        "FILL_CORRECTION",
        "FAILED_CORRECTION",
      ]),
      assigned_at: z.iso.datetime().nullable(),
      due_date: z.iso.datetime().nullable(),
      revisions: z.array(
        z.object({
          id: z.string(),
          status: z.string(),
          applicant_form_assignment_id: z.string().nullable(),
          internal_deadline: z.iso.datetime().nullable(),
          applicant_form_assignment: z
            .object({
              id: z.string(),
              status: z.string(),
            })
            .nullable(),
          corrections: z.array(
            z.object({
              id: z.string(),
              revision_id: z.string(),
              field_corrections: z.array(
                z.object({
                  id: z.string(),
                  correction_id: z.string(),
                  status: z.enum(["APPROVED", "DENIED", "IN_PROCESS"]),
                  comments: z.array(
                    z.object({
                      text: z.string(),
                      created_by: z.object({
                        id: z.string(),
                      }),
                    }),
                  ),
                  filled_field_id: z.string(),
                  filled_fields: z
                    .array(
                      z.object({
                        id: string(),
                        value: z
                          .string()
                          .or(z.number())
                          .or(z.array(z.string()))
                          .or(
                            z.object({
                              start: z.string(),
                              end: z.string(),
                            }),
                          ),
                      }),
                    )
                    .nullable(),
                }),
              ),
              comments: z.array(
                z.object({
                  text: z.string(),
                }),
              ),
            }),
          ),
        }),
      ),
    }),
  ),
  target_user_type: z.string().nullable(),
});

export type FormType = z.infer<typeof FormSchema>;

export type Form = z.infer<typeof FormSchema>;
export type Step = Form["steps"][number];
export type Section = Step["sections"][number];
export type Field = Section["fields"][number];

export const FormDetailResponseSchema = FormSchema;
export type FormDetailResponse = z.infer<typeof FormDetailResponseSchema>;

export const FORM_ASSIGNMENTS_STATUS = {
  ASSIGNED: "ASSIGNED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  SUBMITTED: "SUBMITTED",
  CORRECTION: "CORRECTION",
  FILL_CORRECTION: "FILL_CORRECTION",
  FAILED_CORRECTION: "FAILED_CORRECTION",
  PENDING: "PENDING",
} as const;
